import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import StyleHeroBanner from "../components/StyleHeroBanner";
import StyleThumbnails from "../components/StyleThumbnails";
import Tabs from "../components/Tabs";
import { ApiContext, NavigationContext, UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { getPageTitle } from "../resources/routes";
import { InteriorStyle, UpgradeStyle } from "../types";
import { scrollToTop } from "../util";
import Rendivo from "../util/rendivo";

const INTERIOR_TAB_TITLE = "Interior Colour Scheme";
const KITCHEN_TAB_TITLE = "Cabinetry Colours";
const FLOORING_TAB_TITLE = "Main Flooring";

const ChooseInteriorStyleScreen = observer(() => {
  const userContext = useContext(UserContext);
  const apiContext = useContext(ApiContext);
  const navContext = useContext(NavigationContext);
  const windowSize = useWindowSize();
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const [tabTitles, setTabTitles] = useState([INTERIOR_TAB_TITLE]);

  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    scrollToTop();
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();
    navContext.setFooterModePrimary();
    navContext.setNextButtonEnabled(true);

    navContext.setOnNavigateBack(() => {
      // Reset selection when the user goes back
      userContext.setSelectedInteriorStyle(null);
      userContext.resetSelectedFlooringStyle();
    });

    // Select default option
    if (userContext.selectedInteriorStyle) {
      onClickInteriorColour(userContext.selectedInteriorStyle);
    } else if (apiContext.exteriorStyleResults.length) {
      onClickInteriorColour(apiContext.interiorStyleResults[0]);
    }
    return () => {
      navContext.resetBackBtnOnClick();
    };
  }, []);

  useEffect(() => {
    navContext.resetNextBtn();
    navContext.resetBackBtnOnClick();
    if (tabIndex < tabTitles.length - 1) {
      navContext.setNextBtnOnClick(() => {
        onChangeTabIndex(tabIndex + 1);
      });
    }
    if (tabIndex > 0) {
      navContext.setBackBtnOnClick(() => {
        setTabIndex(tabIndex - 1);
      });
    }
  }, [tabIndex, tabTitles]);

  useEffect(() => {
    navContext.setSubtotalVisible(userContext.subtotalPrice > 0);
  }, [userContext.subtotalPrice]);

  const onClickInteriorColour = (interiorStyle: InteriorStyle) => {
    // If the interior style changes, we need to try and keep the user's kitchen and flooring selections
    let kitchenStyle = null;
    let flooringStyle = null;
    if (
      userContext.selectedInteriorStyle &&
      userContext.selectedInteriorStyle.id !== interiorStyle.id
    ) {
      const currentKitchenStyle = userContext.selectedKitchenStyle.get(
        userContext.selectedInteriorStyle.id
      );
      const currentFlooringStyle = userContext.selectedFlooringStyle.get(
        userContext.selectedInteriorStyle.id
      );

      if (currentKitchenStyle) {
        const matchingKitchenStyle = interiorStyle.kitchen_options.find(
          (item) =>
            item.title.toLowerCase() === currentKitchenStyle.title.toLowerCase()
        );
        if (matchingKitchenStyle) {
          kitchenStyle = matchingKitchenStyle;
        }
      }

      if (currentFlooringStyle) {
        const matchingFlooringStyle = interiorStyle.flooring_options.find(
          (item) =>
            item.title.toLowerCase() ===
            currentFlooringStyle.title.toLowerCase()
        );
        if (matchingFlooringStyle) {
          flooringStyle = matchingFlooringStyle;
        }
      }
    }
    if (!kitchenStyle && interiorStyle.kitchen_options.length > 0) {
      kitchenStyle = interiorStyle.kitchen_options[0];
    }
    if (kitchenStyle) {
      userContext.setSelectedKitchenStyle(interiorStyle.id, kitchenStyle);
    }

    if (!flooringStyle && interiorStyle.flooring_options.length > 0) {
      flooringStyle = interiorStyle.flooring_options[0];
    }
    if (flooringStyle) {
      userContext.setSelectedFlooringStyle(interiorStyle.id, flooringStyle);
    }

    userContext.setSelectedInteriorStyle(interiorStyle);

    const newTabTitles = [INTERIOR_TAB_TITLE];
    if (interiorStyle.kitchen_options.length > 0) {
      newTabTitles.push(KITCHEN_TAB_TITLE);
    }
    if (interiorStyle.flooring_options.length > 0) {
      newTabTitles.push(FLOORING_TAB_TITLE);
    }
    setTabTitles(newTabTitles);
  };

  const onClickKitchenStyle = (kitchenStyle: UpgradeStyle) => {
    if (userContext.selectedInteriorStyle) {
      userContext.setSelectedKitchenStyle(
        userContext.selectedInteriorStyle.id,
        kitchenStyle
      );
    }
  };

  const onClickFlooringStyle = (flooringStyle: UpgradeStyle) => {
    if (userContext.selectedInteriorStyle) {
      userContext.setSelectedFlooringStyle(
        userContext.selectedInteriorStyle.id,
        flooringStyle
      );
    }
  };

  const onChangeTabIndex = (index: number) => {
    if (index !== tabIndex) {
      setSlideIndex(0);
      setTabIndex(index);
    }
  };

  const getSliderItems = () => {
    if (userContext.selectedInteriorStyle) {
      if (tabTitles[tabIndex] === INTERIOR_TAB_TITLE) {
        // Colour Scheme tab shows all renders
        return userContext.selectedInteriorStyle.slider_items;
      }

      if (tabTitles[tabIndex] === KITCHEN_TAB_TITLE) {
        // Kitchen Tab shows only shows Kitchen and Bathroom
        return userContext.selectedInteriorStyle.slider_items.filter(
          (item) => item.type !== "bedroom"
        );
      }

      if (tabTitles[tabIndex] === FLOORING_TAB_TITLE) {
        // Flooring Tab shows only shows Kitchen and Bedroom
        return userContext.selectedInteriorStyle.slider_items.filter(
          (item) => item.type !== "bathroom"
        );
      }
    }

    return [];
  };

  const getTagline = () => {
    const sliderItems = getSliderItems();
    if (userContext.selectedInteriorStyle && sliderItems.length >= slideIndex) {
      return sliderItems[slideIndex].title;
    }

    return "";
  };

  const getInteriorRenders = () => {
    const selectedInterior = userContext.selectedInteriorStyle;

    if (selectedInterior) {
      const selectedKitchenStyle = userContext.selectedKitchenStyle.get(
        selectedInterior.id
      );
      const selectedFlooringStyle = userContext.selectedFlooringStyle.get(
        selectedInterior.id
      );
      return getSliderItems()
        .filter((item) => item.rendivo_id !== null || item.image !== null)
        .map((item) => {
          if (item.rendivo_id !== null) {
            // Inclusions can override the kitchen image, need to support that here.
            let rendivoProductId = item.rendivo_id;
            let kitchenId = "";
            let flooringId = "";
            if (
              item.type === "kitchen" &&
              userContext.selectedInclusion &&
              userContext.selectedInclusion.kitchen_rendivo_id
            ) {
              rendivoProductId =
                userContext.selectedInclusion.kitchen_rendivo_id;
            }

            if (selectedKitchenStyle) {
              kitchenId = selectedKitchenStyle.rendivo_id;
            }
            if (selectedFlooringStyle) {
              flooringId = selectedFlooringStyle.rendivo_id;
            }

            return Rendivo.getExteriorRender(
              1640,
              rendivoProductId,
              null,
              selectedInterior.rendivo_id,
              kitchenId,
              flooringId,
              "",
              "",
              false
            );
          }else if(item.image !== null){
            return item.image;
          }else{
            return "";
          }
        });
    }

    return [];
  };

  const renderInteriorColours = () => {
    const selectedInterior = userContext.selectedInteriorStyle;
    if (!selectedInterior) {
      return <></>;
    }

    return (
      <StyleThumbnails
        imageStyle={"short"}
        activeStyle={windowSize.width > 768 ? "white" : "transparent"}
        items={apiContext.interiorStyleResults}
        selectedId={selectedInterior.id}
        onClickItem={(index) =>
          onClickInteriorColour(apiContext.interiorStyleResults[index])
        }
      />
    );
  };

  const renderKitchenStyles = () => {
    const selectedInterior = userContext.selectedInteriorStyle;
    if (!selectedInterior) {
      return <></>;
    }
    const selectedKitchenStyle = userContext.selectedKitchenStyle.get(
      selectedInterior.id
    );
    if (!selectedKitchenStyle) {
      return <></>;
    }

    let options = selectedInterior.kitchen_options.map((option) => {
      if (
        userContext.selectedHome &&
        userContext.selectedHome.kitchen_upgrades[option.id] !== undefined
      ) {
        return {
          ...option,
          price: userContext.selectedHome.kitchen_upgrades[option.id],
        };
      } else if (
        userContext.selectedHouseLand &&
        userContext.selectedHouseLand.kitchen_upgrades[option.id] !== undefined
      ) {
        return {
          ...option,
          price: userContext.selectedHouseLand.kitchen_upgrades[option.id],
        };
      }
      return {
        ...option,
        price: 0,
      };
    });

    // We hide the two-tone option when viewing the Bathroom image
    const indexOfBathroom = getSliderItems()
      .map((item) => item.type)
      .indexOf("bathroom");
    if (slideIndex === indexOfBathroom) {
      options = options.filter(
        (item) => item.title.toLowerCase() !== "two-tone"
      );
    }

    return (
      <StyleThumbnails
        imageStyle={"short"}
        activeStyle={windowSize.width > 768 ? "white" : "transparent"}
        showPrice
        hideIncluded
        items={options}
        selectedId={selectedKitchenStyle.id}
        onClickItem={(index) =>
          onClickKitchenStyle(selectedInterior.kitchen_options[index])
        }
      />
    );
  };

  const renderFlooringStyles = () => {
    const selectedInterior = userContext.selectedInteriorStyle;
    if (!selectedInterior) {
      return <></>;
    }
    const selectedFlooringStyle = userContext.selectedFlooringStyle.get(
      selectedInterior.id
    );
    if (!selectedFlooringStyle) {
      return <></>;
    }

    let options = selectedInterior.flooring_options.map((option) => {
      if (
        userContext.selectedHome &&
        userContext.selectedHome.flooring_upgrades[option.id] !== undefined
      ) {
        return {
          ...option,
          price: userContext.selectedHome.flooring_upgrades[option.id],
        };
      } else if (
        userContext.selectedHouseLand &&
        userContext.selectedHouseLand.flooring_upgrades[option.id] !== undefined
      ) {
        return {
          ...option,
          price: userContext.selectedHouseLand.flooring_upgrades[option.id],
        };
      }
      return {
        ...option,
        price: 0,
      };
    });

    // We hide the two-tone option when viewing the Bathroom image
    const indexOfBedroom = getSliderItems()
      .map((item) => item.type)
      .indexOf("bedroom");
    if (slideIndex === indexOfBedroom) {
      options = [];
    }

    return (
      <StyleThumbnails
        imageStyle={"short"}
        showPrice
        hideIncluded
        activeStyle={windowSize.width > 768 ? "white" : "transparent"}
        items={options}
        selectedId={selectedFlooringStyle.id}
        onClickItem={(index) =>
          onClickFlooringStyle(selectedInterior.flooring_options[index])
        }
      />
    );
  };

  const renderTabContent = (tabIndex: number) => {
    const selectedInterior = userContext.selectedInteriorStyle;
    if (!selectedInterior) {
      return <></>;
    }

    return (
      <div className={"tab-content container"}>
        <StyleHeroBanner
          showRendivoDisclaimer={apiContext.rendivoDisclaimerVisible}
          onSlide={(index) => setSlideIndex(index)}
          images={getInteriorRenders()}
          tagline={windowSize.width > 768 ? getTagline() : undefined}
          currentSlideIndex={slideIndex}
          usingRendivoImages={false}
        />
        {[renderInteriorColours, renderKitchenStyles, renderFlooringStyles][
          tabIndex
        ]()}
      </div>
    );
  };

  return (
    <div className={"screen screen-choose-interior-style"}>
      <div className={"container"}>
        <h1>{getPageTitle(location.pathname)}</h1>
        <p className={"subtitle"}>
          Choose an interior colour scheme you’ll love to live in each and every
          day. Inspired by the natural landscape.
        </p>
      </div>

      <Tabs
        headerClassName={"container"}
        titles={tabTitles}
        renderContent={renderTabContent}
        tabIndex={tabIndex}
        onChangeTabIndex={onChangeTabIndex}
      />
    </div>
  );
});

export default ChooseInteriorStyleScreen;
